import React, { useEffect, useRef, useState } from "react";
import { resolveAnyStaticUrl } from "../../urlConfig";

const formatMoney = (value, currency = "RUB") => {
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: currency || "RUB",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return `${Number(value || 0).toFixed(2)} ${currency || "RUB"}`;
  }
};

const ItemRow = ({
  i,
  item,
  currency,
  locking,
  onChangeField,
  onChangeMeta,
  onCommit,
  onRemove,
  onLock,
  onUnlock,
  onAttachProduct,
}) => {
  const path = (field) => `items[${i}].${field}`;
  const disabled = (field) => Boolean(locking?.[path(field)]);

  const finishEdit = (field) => {
    onUnlock(path(field));
    onCommit();
  };

  const imageUrl = item.imageUrl
    ? resolveAnyStaticUrl(item.imageUrl)
    : "";

  const showImage = Boolean(item.meta?.showImage);
  const showProductLink = Boolean(item.meta?.showProductLink);
  const showSceneLink = Boolean(item.meta?.showSceneLink);
  const hasCatalogProduct = Boolean(item.productId);
  const catalogHint = item.meta?.catalogProduct || {};
  const waitsForCatalog =
    !hasCatalogProduct &&
    (showImage || showProductLink || catalogHint.attachLater);

  return (
    <article className="offer-item">
      <div className="offer-item__number">
        {String(i + 1).padStart(2, "0")}
      </div>

      <div className="offer-item__main">
        <div className="offer-item__top">
          <div className="offer-item__identity">
            {imageUrl ? (
              <div
                className={`offer-item__thumb ${showImage ? "" : "is-muted"}`}
              >
                <img src={imageUrl} alt="" />
                {!showImage && <span>Скрыто в PDF</span>}
              </div>
            ) : (
              <div
                className={`offer-item__thumb offer-item__thumb--empty ${
                  showImage ? "is-waiting" : ""
                }`}
              >
                <span>{showImage ? "Фото будет взято из каталога" : "Без фото"}</span>
              </div>
            )}

            <div className="offer-item__name-fields">
              <label className="offer-item__field">
                <span>Наименование</span>
                <input
                  value={item.name || ""}
                  onChange={(event) =>
                    onChangeField(i, "name", event.target.value)
                  }
                  onFocus={() => onLock(path("name"))}
                  onBlur={() => finishEdit("name")}
                  disabled={disabled("name")}
                  placeholder="Товар или услуга"
                />
              </label>

              <label className="offer-item__field">
                <span>Описание позиции</span>
                <textarea
                  value={item.meta?.details || ""}
                  onChange={(event) =>
                    onChangeMeta(i, "details", event.target.value)
                  }
                  onFocus={() => onLock(path("meta.details"))}
                  onBlur={() => finishEdit("meta.details")}
                  disabled={disabled("meta.details")}
                  placeholder="Материал, размеры, покрытие, комплектация и другие характеристики"
                  rows={3}
                />
              </label>
            </div>
          </div>

          <button
            type="button"
            className="offer-item__remove"
            onClick={() => onRemove(i)}
            aria-label={`Удалить позицию ${i + 1}`}
            title="Удалить позицию"
          >
            ×
          </button>
        </div>

        <div className="offer-item__catalog">
          <div>
            <span
              className={`offer-item__catalog-status ${
                hasCatalogProduct
                  ? "offer-item__catalog-status--linked"
                  : waitsForCatalog
                    ? "offer-item__catalog-status--waiting"
                    : ""
              }`}
            >
              {hasCatalogProduct
                ? "Товар каталога привязан"
                : waitsForCatalog
                  ? "Место под фото и ссылку подготовлено"
                  : "Позиция пока не связана с каталогом"}
            </span>

            {!hasCatalogProduct &&
              (catalogHint.query || catalogHint.article || catalogHint.slug) && (
                <small>
                  Подсказка для поиска: {[
                    catalogHint.query,
                    catalogHint.article,
                    catalogHint.slug,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </small>
              )}
          </div>

          <button
            type="button"
            className="offer-item__catalog-button"
            onClick={() => onAttachProduct(i)}
          >
            {hasCatalogProduct ? "Заменить товар" : "Выбрать товар из каталога"}
          </button>
        </div>

        <div className="offer-item__pricing">
          <label className="offer-item__field offer-item__field--small">
            <span>Количество</span>
            <input
              type="number"
              min="0"
              step="any"
              value={item.qty ?? 1}
              onChange={(event) =>
                onChangeField(i, "qty", event.target.value)
              }
              onFocus={() => onLock(path("qty"))}
              onBlur={() => finishEdit("qty")}
              disabled={disabled("qty")}
            />
          </label>

          <label className="offer-item__field offer-item__field--small">
            <span>Единица</span>
            <input
              value={item.meta?.unit || "шт."}
              onChange={(event) =>
                onChangeMeta(i, "unit", event.target.value)
              }
              onFocus={() => onLock(path("meta.unit"))}
              onBlur={() => finishEdit("meta.unit")}
              disabled={disabled("meta.unit")}
              placeholder="шт."
            />
          </label>

          <label className="offer-item__field offer-item__field--price">
            <span>Цена за единицу</span>
            <div className="offer-item__money-input">
              <input
                type="number"
                min="0"
                step="any"
                value={item.price ?? 0}
                onChange={(event) =>
                  onChangeField(i, "price", event.target.value)
                }
                onFocus={() => onLock(path("price"))}
                onBlur={() => finishEdit("price")}
                disabled={disabled("price")}
              />
              <span>{currency || "RUB"}</span>
            </div>
          </label>

          <div className="offer-item__sum">
            <span>Сумма позиции</span>
            <strong>
              {formatMoney(
                Number(item.qty || 0) * Number(item.price || 0),
                currency
              )}
            </strong>
          </div>
        </div>

        <div className="offer-item__media">
          <div className="offer-item__media-title">
            <span>Медиа и ссылки в PDF</span>
            <small>
              Их можно включить заранее, а сам товар привязать позже.
            </small>
          </div>

          <div className="offer-item__toggles">
            <label className="offer-switch">
              <input
                type="checkbox"
                checked={showImage}
                onChange={(event) => {
                  onChangeMeta(i, "showImage", event.target.checked);
                  setTimeout(onCommit, 0);
                }}
              />
              <span />
              Фото
            </label>

            <label className="offer-switch">
              <input
                type="checkbox"
                checked={showProductLink}
                onChange={(event) => {
                  onChangeMeta(i, "showProductLink", event.target.checked);
                  setTimeout(onCommit, 0);
                }}
              />
              <span />
              Ссылка на товар
            </label>

            <label className="offer-switch">
              <input
                type="checkbox"
                checked={showSceneLink}
                onChange={(event) => {
                  onChangeMeta(i, "showSceneLink", event.target.checked);
                  setTimeout(onCommit, 0);
                }}
              />
              <span />
              Ссылка на 3D-сцену
            </label>
          </div>

          {(showProductLink || showSceneLink) && (
            <div className="offer-item__links">
              {showProductLink && (
                <label className="offer-item__field">
                  <span>Ссылка на товар</span>
                  <input
                    type="url"
                    value={item.meta?.productUrl || ""}
                    onChange={(event) =>
                      onChangeMeta(i, "productUrl", event.target.value)
                    }
                    onFocus={() => onLock(path("meta.productUrl"))}
                    onBlur={() => finishEdit("meta.productUrl")}
                    disabled={disabled("meta.productUrl")}
                    placeholder="Выберите товар из каталога или вставьте ссылку вручную"
                  />
                </label>
              )}

              {showSceneLink && (
                <label className="offer-item__field">
                  <span>Временная ссылка на 3D-сцену</span>
                  <input
                    type="url"
                    value={item.meta?.sceneUrl || ""}
                    onChange={(event) =>
                      onChangeMeta(i, "sceneUrl", event.target.value)
                    }
                    onFocus={() => onLock(path("meta.sceneUrl"))}
                    onBlur={() => finishEdit("meta.sceneUrl")}
                    disabled={disabled("meta.sceneUrl")}
                    placeholder="https://alotb.ru/scene/..."
                  />
                </label>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default function OfferItemsTable({
  items,
  currency,
  locking,
  onChange,
  onLock,
  onUnlock,
  onOpenPicker,
  onAttachProduct,
}) {
  const [local, setLocal] = useState(items || []);
  const localRef = useRef(items || []);

  useEffect(() => {
    const next = Array.isArray(items) ? items : [];
    localRef.current = next;
    setLocal(next);
  }, [items]);

  const updateLocal = (next) => {
    localRef.current = next;
    setLocal(next);
  };

  const commit = async (next = localRef.current) => {
    updateLocal(next);
    await onChange(
      next.map((item) => ({
        ...item,
        qty: Number(item.qty || 0),
        price: Number(item.price || 0),
      }))
    );
  };

  const addManual = () => {
    const next = [
      ...localRef.current,
      {
        name: "",
        qty: 1,
        price: 0,
        sum: 0,
        imageUrl: null,
        meta: {
          unit: "шт.",
          details: "",
          dimensions: "",
          showImage: false,
          productUrl: "",
          showProductLink: false,
          sceneUrl: "",
          showSceneLink: false,
          catalogProduct: {
            productId: "",
            slug: "",
            article: "",
            query: "",
            attachLater: true,
          },
        },
      },
    ];
    commit(next);
  };

  const removeRow = (index) => {
    commit(localRef.current.filter((_, itemIndex) => itemIndex !== index));
  };

  const changeField = (index, field, value) => {
    const next = [...localRef.current];
    next[index] = {
      ...(next[index] || {}),
      [field]: value,
    };
    updateLocal(next);
  };

  const changeMeta = (index, field, value) => {
    const next = [...localRef.current];
    next[index] = {
      ...(next[index] || {}),
      meta: {
        ...(next[index]?.meta || {}),
        [field]: value,
      },
    };
    updateLocal(next);
  };

  return (
    <div className="offer-table">
      <div className="offer-section-heading">
        <div>
          <span className="offer-section-heading__index">04</span>
          <div>
            <h3>Позиции предложения</h3>
            <p>
              Добавьте работы, товары и характеристики. Суммы рассчитываются
              автоматически.
            </p>
          </div>
        </div>

        <span className="offer-table__count">
          {local.length} {local.length === 1 ? "позиция" : "позиций"}
        </span>
      </div>

      <div className="offer-table__body">
        {local.length ? (
          local.map((item, index) => (
            <ItemRow
              key={String(item.itemId || item.productId || index)}
              i={index}
              item={item}
              currency={currency}
              locking={locking}
              onChangeField={changeField}
              onChangeMeta={changeMeta}
              onCommit={() => commit(localRef.current)}
              onRemove={removeRow}
              onLock={onLock}
              onUnlock={onUnlock}
              onAttachProduct={onAttachProduct}
            />
          ))
        ) : (
          <div className="offer-table__empty">
            <strong>В предложении пока нет позиций</strong>
            <span>
              Добавьте строку вручную, импортируйте JSON или выберите готовый
              товар из каталога.
            </span>
          </div>
        )}
      </div>

      <div className="offer-table__footer">
        <button
          type="button"
          className="offer-table__add offer-table__add--primary"
          onClick={addManual}
        >
          <span>+</span>
          Ввести вручную
        </button>

        <button
          type="button"
          className="offer-table__add"
          onClick={onOpenPicker}
        >
          <span>+</span>
          Выбрать из каталога
        </button>
      </div>
    </div>
  );
}
